// Router.js
import { useRoutes, Navigate } from 'react-router-dom';
import ProtectedRoute, { PERMISSIONS } from './protectedRoute';
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
import BlogPage from './pages/BlogPage';
import UserPage from './pages/UserPage';
import LoginPage from './pages/LoginPage';
import HapRegistrationPage from './pages/HapRegistrationPage';
import Page404 from './pages/Page404';
import ProductsPage from './pages/ProductsPage';
import DashboardAppPage from './pages/DashboardAppPage';
import ConfigurationPage from './pages/ConfigurationPage';
import EmailNotificationsPage from './pages/EmailNotificationsPage';
import EventsPage from './pages/EventsPage';
import AttendancePage from './pages/AttendancePage';
import PushNotificationsPage from './pages/PushNotificationPage';
import NewEventPage from './pages/NewEventPage';
import NewUserPage from './pages/NewUserPage';
import NewPushNotificationPage from './pages/NewPushNotificationPage';
import CategoriesPage from './pages/CategoriesPage';
import GroupsPage from './pages/GroupsPage';
import NewCategoryPage from './pages/NewCategoryPage';
import NewGroupPage from './pages/NewGroupPage';
import WinnersReportPage from './pages/WinnersReportPage';
import ReportsPage from './pages/ReportsPage';
import PreviousEventsPage from './pages/PreviousEventsPage';
import PreviousAttendancePage from './pages/PreviousAttendancePage';
import ReservationCategoriesPage from './pages/ReservationCategoriesPage';
import NewReservationCategoryPage from './pages/NewReservationCategoryPage';
import LanesPage from './pages/LanesPage';
import CouponsPage from './pages/CouponsPage';
import NewLanePage from './pages/NewLanePage';
import AvailabilityPage from './pages/AvailabilityPage';
import ReservationsPage from './pages/ReservationsPage';
import EmployeesPage from './pages/EmployeesPage';
import NewEmployeePage from './pages/NewEmployeePage';
import LaneBlocksPage from './pages/LaneBlocksPage';
import RecurringReservationsPage from './pages/RecurringReservationsPage';
import CoursesPage from './pages/CoursesPage';
import ReservationDetailsPage from './pages/ReservationDetailsPage';
import ReservationActivityPage from './pages/ReservationActivityPage';
import MembershipsPage from './pages/MembershipsPage';
import NewMembershipPage from './pages/NewMembershipPage';
import MembershipRequestsPage from './pages/MembershipRequestsPage';
import LayawaysPage from './pages/LayawaysPage';
import LayawayDetailsPage from './pages/LayawayDetailsPage';
import LayawayPaymentsPage from './pages/LayawayPaymentsPage';
import MyLayawaysPage from './pages/public/MyLayawaysPage';
import WaitlistPage from './pages/WaitlistPage';
import MyWaitlistPage from './pages/public/MyWaitlistPage';
import TransfersPage from './pages/TransfersPage';
import TransferDetailsPage from './pages/TransferDetailsPage';
import FflDealersPage from './pages/FflDealersPage';
import TransferRequestPage from './pages/public/TransferRequestPage';
import ServiceOrdersPage from './pages/ServiceOrdersPage';
import MakeReservationPage from './pages/public/MakeReservationPage';
import PartnerPortalPage from './pages/public/PartnerPortalPage';
import PartnersPage from './pages/PartnersPage';
import InstructorPayPage from './pages/InstructorPayPage';
import ResetPinPage from './pages/public/ResetPinPage';
import MyReservationsPage from './pages/public/MyReservationsPage';
import PayReservationPage from './pages/public/PayReservationPage';
import CsccVendorPayPage from './pages/public/CsccVendorPayPage';
import CsccVendorsPage from './pages/CsccVendorsPage';
import RafflesPage from './pages/RafflesPage';
import RafflePage from './pages/public/RafflePage';
import StorePage from './pages/public/StorePage';

export default function Router() {
  const isAuthenticated = localStorage.getItem('token');

  const routes = useRoutes([
    {
      path: '/',
      element: isAuthenticated ? <Navigate to="/dashboard/user" replace /> : <Navigate to="/login" replace />,
      index: true,
    },
    {
      path: 'login',
      element: <LoginPage />,
    },
    {
      path: 'hap-registration',
      element: <HapRegistrationPage />,
    },
    {
      path: 'make-reservation',
      element: <MakeReservationPage />,
    },
    {
      path: 'reset-pin',
      element: <ResetPinPage />,
    },
    {
      path: 'my-reservations',
      element: <MyReservationsPage />,
    },
    {
      // Link de pago enviado por SMS/email cuando el staff inscribe al cliente
      path: 'pay',
      element: <PayReservationPage />,
    },
    {
      // Link de pago para vendedores de la Caribbean Sporting Clays Cup
      // (emprendedores, artesanos, carpas de comida, food trucks): /vendor-pay?t=TOKEN
      path: 'vendor-pay',
      element: <CsccVendorPayPage />,
    },
    {
      // Sorteos: compra en línea (/sorteo?r=TOKEN) o link de pago de una
      // venta reservada por el staff (/sorteo?t=TOKEN).
      path: 'sorteo',
      element: <RafflePage />,
    },
    {
      // Portal de aliados (armerías que inscriben a sus clientes en cursos).
      // Se movió a /armeros (2026-09-24); /aliados redirige para no romper
      // los links que ya se repartieron.
      path: 'armeros',
      element: <PartnerPortalPage />,
    },
    {
      path: 'aliados',
      element: <Navigate to="/armeros" replace />,
    },
    {
      path: 'store',
      element: <StorePage />,
    },
    {
      // Layaways de armas: el cliente ve sus layaways y abona desde el teléfono
      path: 'layaways',
      element: <MyLayawaysPage />,
    },
    {
      // Lista de espera de productos: el cliente se anota y ve su posición
      path: 'waitlist',
      element: <MyWaitlistPage />,
    },
    {
      // Solicitud pública de transferencia FFL a FFL (para la página web)
      path: 'transfer',
      element: <TransferRequestPage />,
    },
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        // { path: 'app', element: <ProtectedRoute element={<DashboardAppPage />} /> },
        { path: 'configuration', element: <ProtectedRoute element={<ConfigurationPage />} requiredPermission={PERMISSIONS.VIEW_CONFIG} /> },
        { path: 'email-notifications', element: <ProtectedRoute element={<EmailNotificationsPage />} requiredPermission={PERMISSIONS.VIEW_CONFIG} /> },
        { path: 'user', element: <ProtectedRoute element={<UserPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'user/information', element: <ProtectedRoute element={<NewUserPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'events', element: <ProtectedRoute element={<EventsPage />} requiredPermission={PERMISSIONS.VIEW_EVENTS} /> },
        { path: 'events/attendance', element: <ProtectedRoute element={<AttendancePage />} requiredPermission={PERMISSIONS.VIEW_EVENTS} /> },
        { path: 'previous-events/previous-attendance', element: <ProtectedRoute element={<PreviousAttendancePage />} requiredPermission={PERMISSIONS.VIEW_EVENTS} /> },
        { path: 'events/information', element: <ProtectedRoute element={<NewEventPage />} requiredPermission={PERMISSIONS.VIEW_EVENTS} /> },
        { path: 'push-notifications', element: <ProtectedRoute element={<PushNotificationsPage />} requiredPermission={PERMISSIONS.VIEW_EVENTS} /> },
        { path: 'push-notifications/information', element: <ProtectedRoute element={<NewPushNotificationPage />} requiredPermission={PERMISSIONS.VIEW_EVENTS} /> },
        { path: 'categories', element: <ProtectedRoute element={<CategoriesPage />} /> },
        { path: 'categories/information', element: <ProtectedRoute element={<NewCategoryPage />} /> },
        { path: 'groups/information', element: <ProtectedRoute element={<NewGroupPage />} /> },
        { path: 'groups', element: <ProtectedRoute element={<GroupsPage />} /> },
        { path: 'products', element: <ProtectedRoute element={<ProductsPage />} /> },
        { path: 'winner-reports', element: <ProtectedRoute element={<WinnersReportPage />} /> },
        { path: 'reports', element: <ProtectedRoute element={<ReportsPage />} requiredPermission={PERMISSIONS.VIEW_CALENDAR} /> },
        { path: 'partners', element: <ProtectedRoute element={<PartnersPage />} requiredPermission={PERMISSIONS.VIEW_PAYMENTS} /> },
        { path: 'instructor-pay', element: <ProtectedRoute element={<InstructorPayPage />} requiredPermission={PERMISSIONS.VIEW_INSTRUCTOR_PAY} /> },
        { path: 'cscc-vendors', element: <ProtectedRoute element={<CsccVendorsPage />} requiredPermission={PERMISSIONS.VIEW_CSCC_VENDORS} /> },
        { path: 'raffles', element: <ProtectedRoute element={<RafflesPage />} requiredPermission={PERMISSIONS.VIEW_RAFFLES} /> },
        { path: 'raffle', element: <Navigate to="/dashboard/raffles" replace /> },
        { path: 'sorteos', element: <Navigate to="/dashboard/raffles" replace /> },
        { path: 'previous-events', element: <ProtectedRoute element={<PreviousEventsPage />} requiredPermission={PERMISSIONS.VIEW_EVENTS} /> },
        { path: 'blog', element: <ProtectedRoute element={<BlogPage />} requiredPermission={PERMISSIONS.VIEW_EVENTS} /> },
        { path: 'reservations', element: <ProtectedRoute element={<ReservationsPage />} requiredPermission={PERMISSIONS.VIEW_CALENDAR} /> },
        { path: 'reservations/details', element: <ProtectedRoute element={<ReservationDetailsPage />} requiredPermission={PERMISSIONS.VIEW_CALENDAR} /> },
        { path: 'reservations/activity', element: <ProtectedRoute element={<ReservationActivityPage />} requiredPermission={PERMISSIONS.VIEW_CALENDAR} /> },
        { path: 'reservation-categories', element: <ProtectedRoute element={<ReservationCategoriesPage />} requiredPermission={PERMISSIONS.VIEW_CATEGORIES} /> },
        { path: 'reservation-categories/information', element: <ProtectedRoute element={<NewReservationCategoryPage />} requiredPermission={PERMISSIONS.VIEW_CATEGORIES} /> },
        { path: 'lanes', element: <ProtectedRoute element={<LanesPage />} requiredPermission={PERMISSIONS.VIEW_CATEGORIES} /> },
        { path: 'coupons', element: <ProtectedRoute element={<CouponsPage />} requiredPermission={PERMISSIONS.VIEW_CATEGORIES} /> },
        { path: 'lanes/information', element: <ProtectedRoute element={<NewLanePage />} requiredPermission={PERMISSIONS.VIEW_CATEGORIES} /> },
        { path: 'availability', element: <ProtectedRoute element={<AvailabilityPage />} requiredPermission={PERMISSIONS.EDIT_CALENDAR} /> },
        { path: 'employees', element: <ProtectedRoute element={<EmployeesPage />} requiredPermission={PERMISSIONS.VIEW_EMPLOYEES} /> },
        { path: 'employees/information', element: <ProtectedRoute element={<NewEmployeePage />} requiredPermission={PERMISSIONS.EDIT_EMPLOYEES} /> },
        { path: 'lane-blocks', element: <ProtectedRoute element={<LaneBlocksPage />} requiredPermission={PERMISSIONS.EDIT_CALENDAR} /> },
        { path: 'recurring-reservations', element: <ProtectedRoute element={<RecurringReservationsPage />} requiredPermission={PERMISSIONS.EDIT_CALENDAR} /> },
        { path: 'courses', element: <ProtectedRoute element={<CoursesPage />} requiredPermission={PERMISSIONS.VIEW_CALENDAR} /> },
        { path: 'memberships', element: <ProtectedRoute element={<MembershipsPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'memberships/information', element: <ProtectedRoute element={<NewMembershipPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'membership-requests', element: <ProtectedRoute element={<MembershipRequestsPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'layaways', element: <ProtectedRoute element={<LayawaysPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'layaways/details', element: <ProtectedRoute element={<LayawayDetailsPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'layaways/payments', element: <ProtectedRoute element={<LayawayPaymentsPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'waitlist', element: <ProtectedRoute element={<WaitlistPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'transfers', element: <ProtectedRoute element={<TransfersPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'transfers/details', element: <ProtectedRoute element={<TransferDetailsPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'ffl-dealers', element: <ProtectedRoute element={<FflDealersPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
        { path: 'service-orders', element: <ProtectedRoute element={<ServiceOrdersPage />} requiredPermission={PERMISSIONS.VIEW_MEMBERS} /> },
      ],
    },
    {
      element: <SimpleLayout />,
      children: [
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> }
      ]
    }
  ]);

  return routes;
}
